import { BASE_SCHEMA_KEYS } from './baseLogObject.js';
import {
  MESSAGE_PATTERN_KEYS,
  patternPrechecks,
  piiPatterns,
  STACK_PATTERN_KEYS,
  type PiiPatternKey,
} from './redactPIIPatterns.js';

const REDACTED = '[Redacted]';

const SENSITIVE_KEY_PATTERN =
  /^(?:password|passphrase|secret|ssn|socialsecuritynumber|creditcard(?:number)?|cardnumber|pan|email|phone(?:number)?|address|streetaddress|token|accesstoken|refreshtoken|authorization|credential|credentials)$/i;

const FREE_TEXT_KEYS = new Set<string>(['message', 'stack', 'description']);
type FreeTextField = 'message' | 'stack' | 'description';

const BASE_SCHEMA_KEY_SET = new Set<string>(BASE_SCHEMA_KEYS);

/** Only the first 8 KB of stack traces is scanned for inline PII. */
const STACK_REDACT_MAX_LENGTH = 8192;

type RedactResult = { value: unknown; changed: boolean };

/**
 * @param value
 * @returns A result indicating the value was not modified.
 */
const unchanged = (value: unknown): RedactResult => ({ value, changed: false });

/**
 * @param value
 * @returns A result indicating the value was redacted or rebuilt.
 */
const modified = (value: unknown): RedactResult => ({ value, changed: true });

/**
 * @param key
 * @returns Whether `key` is a free-text field scanned for inline PII.
 */
const isFreeTextKey = (key: string): key is FreeTextField =>
  FREE_TEXT_KEYS.has(key);

/**
 * @param field
 * @returns PII pattern keys applicable to that field (`stack` uses a lighter subset).
 */
const getPatternKeys = (field: FreeTextField): readonly PiiPatternKey[] =>
  field === 'stack' ? STACK_PATTERN_KEYS : MESSAGE_PATTERN_KEYS;

/**
 * @param value
 * @param field
 * @returns `{ head }` to scan and `{ tail }` appended unchanged after redaction.
 */
const splitStackScan = (
  value: string,
  field: FreeTextField,
): { head: string; tail: string } => {
  if (field !== 'stack' || value.length <= STACK_REDACT_MAX_LENGTH) {
    return { head: value, tail: '' };
  }
  return {
    head: value.slice(0, STACK_REDACT_MAX_LENGTH),
    tail: value.slice(STACK_REDACT_MAX_LENGTH),
  };
};

/**
 * @param value
 * @param field
 * @returns Redacted string, or the original reference when unchanged.
 */
const redactStringPatterns = (
  value: string,
  field: FreeTextField = 'message',
): RedactResult => {
  const { head, tail } = splitStackScan(value, field);

  const patterns = getPatternKeys(field)
    .filter((key) => patternPrechecks[key](head))
    .map((key) => piiPatterns[key]);

  if (patterns.length === 0) {
    return unchanged(value);
  }

  const redactedHead = patterns.reduce(
    (current, pattern) => current.replace(pattern, REDACTED),
    head,
  );
  const result = redactedHead + tail;

  return result === value ? unchanged(value) : modified(result);
};

/**
 * @param value
 * @returns Redacted Error, or the original reference when unchanged.
 */
const redactErrorValue = (value: Error): RedactResult => {
  const messageResult = redactStringPatterns(value.message, 'message');
  const stackResult = value.stack
    ? redactStringPatterns(value.stack, 'stack')
    : unchanged(value.stack);

  if (!messageResult.changed && !stackResult.changed) {
    return unchanged(value);
  }

  return modified({
    ...value,
    name: value.name,
    message: messageResult.value,
    stack: stackResult.value,
  });
};

/**
 * @param key
 * @param value
 * @returns Redacted string value according to its object key.
 */
const redactStringValue = (key: string, value: string): RedactResult => {
  if (BASE_SCHEMA_KEY_SET.has(key)) return unchanged(value);
  if (SENSITIVE_KEY_PATTERN.test(key)) return modified(REDACTED);
  if (isFreeTextKey(key)) return redactStringPatterns(value, key);
  return unchanged(value);
};

/**
 * @param value
 * @returns Redacted array, or the original reference when unchanged.
 */
const redactArrayValue = (value: unknown[]): RedactResult => {
  let next: unknown[] | undefined;

  value.forEach((item, index) => {
    const result = redactValue(String(index), item);
    if (result.changed) {
      if (!next) next = value.slice();
      next[index] = result.value;
    }
  });

  return next ? modified(next) : unchanged(value);
};

/**
 * @param data
 * @returns Redacted object, or the original reference when unchanged.
 */
function redactObject(data: Record<string, unknown>): RedactResult {
  let next: Record<string, unknown> | undefined;

  Object.entries(data).forEach(([key, value]) => {
    const result = redactValue(key, value);
    if (result.changed) {
      if (!next) next = { ...data };
      next[key] = result.value;
    }
  });

  return next ? modified(next) : unchanged(data);
}

/**
 * @param key
 * @param value
 * @returns Redacted value with a `changed` flag for copy-on-write.
 */
function redactValue(key: string, value: unknown): RedactResult {
  if (key === 'context') {
    return unchanged(value);
  }

  if (value instanceof Error) {
    return redactErrorValue(value);
  }

  if (typeof value === 'string') {
    return redactStringValue(key, value);
  }

  if (Array.isArray(value)) {
    return redactArrayValue(value);
  }

  if (value && typeof value === 'object') {
    return redactObject(value as Record<string, unknown>);
  }

  return unchanged(value);
}

/**
 * Redact PII from a log payload before it is sent to diagnostics.
 *
 * Redaction is key-aware:
 * - **Sensitive keys** (email, ssn, password, token, …) — entire value replaced.
 * - **Free-text keys** (`message`, `stack`, `description`) — inline PII regexes.
 * - **Base schema keys** and **`context`** — pass through unchanged.
 * - **All other keys** — pass through unchanged (structured fields such as `duration`).
 *
 * Returns the original reference when nothing changed (copy-on-write).
 * @param data Object to redact.
 * @returns Redacted object, or the same reference when no redaction was needed.
 */
export const redactPii = <T>(data: T): T => {
  try {
    if (!data || typeof data !== 'object') return data;
    return redactObject(data as Record<string, unknown>).value as T;
  } catch {
    return data;
  }
};
